import type { MatchState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { EntityId, RestartKind, TeamIdx, Vec2 } from '../types';
import type { TeamSize } from '../config/pitchConfig';
import { PITCH } from '../config/pitchConfig';

// Formations (§8.1, GK-exclusive notation; GK = slot 0 when teamSize ≥ 3): 1:"1", 2:"1-1", 3:"2",
// 5:"2-2", 11:"4-4-2". Base positions live in a normalized own-goal-relative frame (x = −1 own goal →
// +1 opponent goal, y = ±1 touchlines); formationTargets scales to the pitch, shifts toward the ball and
// by phase, and mirrors x by attackDir. Pure — no I/O, no RNG.

export type Role = 'GK' | 'DEF' | 'MID' | 'FWD';
interface Slot {
  readonly role: Role;
  readonly base: Vec2; // normalized: x own-goal-relative, y touchline-relative
}

const FORMATIONS: Readonly<Record<TeamSize, readonly Slot[]>> = {
  1: [{ role: 'FWD', base: { x: 0, y: 0 } }],
  2: [
    { role: 'DEF', base: { x: -0.45, y: 0 } },
    { role: 'FWD', base: { x: 0.4, y: 0 } },
  ],
  3: [
    { role: 'GK', base: { x: -0.9, y: 0 } },
    { role: 'DEF', base: { x: -0.2, y: -0.4 } },
    { role: 'FWD', base: { x: 0.3, y: 0.4 } },
  ],
  5: [
    { role: 'GK', base: { x: -0.9, y: 0 } },
    { role: 'DEF', base: { x: -0.45, y: -0.45 } },
    { role: 'DEF', base: { x: -0.45, y: 0.45 } },
    { role: 'FWD', base: { x: 0.35, y: -0.4 } },
    { role: 'FWD', base: { x: 0.35, y: 0.4 } },
  ],
  11: [
    { role: 'GK', base: { x: -0.92, y: 0 } },
    { role: 'DEF', base: { x: -0.6, y: -0.7 } },
    { role: 'DEF', base: { x: -0.6, y: -0.24 } },
    { role: 'DEF', base: { x: -0.6, y: 0.24 } },
    { role: 'DEF', base: { x: -0.6, y: 0.7 } },
    { role: 'MID', base: { x: -0.1, y: -0.7 } },
    { role: 'MID', base: { x: -0.1, y: -0.24 } },
    { role: 'MID', base: { x: -0.1, y: 0.24 } },
    { role: 'MID', base: { x: -0.1, y: 0.7 } },
    { role: 'FWD', base: { x: 0.45, y: -0.3 } },
    { role: 'FWD', base: { x: 0.45, y: 0.3 } },
  ],
};

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** The role of a team's slot (GK = slot 0 for teamSize ≥ 3). */
export function slotRole(teamSize: TeamSize, slot: number): Role {
  return FORMATIONS[teamSize][slot]?.role ?? 'MID';
}

export type TeamPhase = 'ATTACK' | 'DEFEND' | 'TRANSITION';

/**
 * The kicking team's kickoff taker (§6.1): its non-GK entity whose formation slot sits nearest the centre
 * spot — the centre-forward. Deterministic (lowest id breaks ties). Returns null if the team has no outfielder.
 */
export function kickoffTaker(state: MatchState, kickTeam: TeamIdx): EntityId | null {
  const size = state.mode.teamSize;
  const slots = FORMATIONS[size];
  let bestId: EntityId | null = null;
  let bestX = -Infinity; // slot base.x closest to +1 (opponent goal) = most forward
  for (let slot = 0; slot < slots.length; slot++) {
    const s = slots[slot]!;
    if (s.role === 'GK') continue;
    const id = (kickTeam * size + slot) as EntityId;
    const p = state.players[id];
    if (!p || p.sentOff) continue;
    if (s.base.x > bestX) {
      bestX = s.base.x;
      bestId = id;
    }
  }
  return bestId;
}

/**
 * D38: the designated dead-ball taker — the ONLY player whose touch/kick connects on a pending restart.
 * GOAL_KICK belongs to the keeper; anything else to the nearest own-team outfielder at the whistle
 * (ties → lowest id; no outfielders left → the keeper). Deterministic; stored on `state.restart`.
 */
export function restartTaker(state: MatchState, kind: RestartKind, team: TeamIdx, spot: Vec2): EntityId | null {
  const gk = state.players.find((p) => p.team === team && p.isGk && !p.sentOff);
  if (kind === 'GOAL_KICK' && gk) return gk.id;
  let best: EntityId | null = null;
  let bestD = Infinity;
  for (const p of state.players) {
    if (p.team !== team || p.sentOff || p.isGk) continue;
    const d = Math.hypot(p.pos.x - spot.x, p.pos.y - spot.y);
    if (d < bestD - 1e-9 || (Math.abs(d - bestD) <= 1e-9 && (best === null || p.id < best))) {
      bestD = d;
      best = p.id;
    }
  }
  return best ?? gk?.id ?? null;
}

/**
 * Place both teams in kickoff formation and stand the kicking team's taker on the centre spot (§6.1). Pure —
 * mutates player positions/velocities. The kicking team attacks, the other defends (pulled back), and the
 * taker is nudged just onto its own side of centre so a quick kick puts the ball live.
 */
export function placeKickoffFormation(state: MatchState, kickTeam: TeamIdx, cfg: SimConfig): void {
  const other: TeamIdx = kickTeam === 0 ? 1 : 0;
  const kickTargets = formationTargets(state, kickTeam, 'ATTACK', cfg);
  const defTargets = formationTargets(state, other, 'DEFEND', cfg);
  for (const p of state.players) {
    if (p.sentOff) continue;
    const t = (p.team === kickTeam ? kickTargets : defTargets).get(p.id);
    if (t) p.pos = { x: t.x, y: t.y };
    // D43: at the whistle EVERYONE stands in his own half (the attack-shifted 11v11 formation pushed
    // mids across); the taker + circle mate are re-placed below.
    const dir = state.attackDir[p.team];
    if (dir * p.pos.x > -0.5) p.pos.x = -dir * 0.5;
    p.vel = { x: 0, y: 0 };
  }
  const taker = kickoffTaker(state, kickTeam);
  if (taker !== null) {
    const dir = state.attackDir[kickTeam];
    state.players[taker]!.pos = { x: -dir * 0.5, y: 0 }; // just behind the ball on its own side
    state.players[taker]!.facing = dir > 0 ? 0 : Math.PI;
    // D43: a SECOND kicking-team player stands in the circle as the lay-off option — real kickoffs
    // are two players over the ball
    const size = state.mode.teamSize;
    const slots = FORMATIONS[size];
    let mate: EntityId | null = null;
    let bestX = -Infinity;
    for (let slot = 0; slot < slots.length; slot++) {
      const sl = slots[slot]!;
      const id = (kickTeam * size + slot) as EntityId;
      if (sl.role === 'GK' || id === taker) continue;
      const p = state.players[id];
      if (!p || p.sentOff) continue;
      if (sl.base.x > bestX) {
        bestX = sl.base.x;
        mate = id;
      }
    }
    if (mate !== null) {
      state.players[mate]!.pos = { x: -dir * 2.4, y: 2.2 }; // inside the centre circle, own side
      state.players[mate]!.facing = dir > 0 ? 0 : Math.PI;
    }
  }
}

/**
 * Per-entity formation target for `team` this tick — D57-B, agent2d-lite SHAPE-PRESERVING block.
 * The old math lerped every slot TOWARD the ball point (blob football); now the whole block SLIDES
 * with the ball by per-role weights (lines keep their spacing), pushes up in ATTACK, drops off in
 * DEFEND, and DEF/MID are clamped GOAL-SIDE of the ball while defending. All shape numbers live in
 * cfg.ai.SHAPE — team feel is tuned as data, never code.
 */
export function formationTargets(state: MatchState, team: TeamIdx, phase: TeamPhase, cfg: SimConfig): Map<EntityId, Vec2> {
  const size = state.mode.teamSize;
  const pitch = PITCH[size];
  const dir = state.attackDir[team];
  const halfL = pitch.length / 2;
  const halfW = pitch.width / 2;
  const slots = FORMATIONS[size];
  const SH = cfg.ai.SHAPE;
  const phaseShift = phase === 'ATTACK' ? SH.ATTACK_PUSH : phase === 'DEFEND' ? -SH.DEFEND_DROP : 0;
  const ballXNorm = clamp((dir * state.ball.pos.x) / halfL, -1, 1); // ball x in own-goal-relative units
  const ballYNorm = clamp(state.ball.pos.y / halfW, -1, 1);

  const targets = new Map<EntityId, Vec2>();
  for (let slot = 0; slot < slots.length; slot++) {
    const id = (team * size + slot) as EntityId;
    const p = state.players[id];
    if (!p || p.sentOff) continue; // sentOff excluded — remaining N−1 keep their base slots
    const s = slots[slot]!;
    if (s.role === 'GK') {
      targets.set(id, { x: dir * s.base.x * halfL, y: clamp(state.ball.pos.y * 0.2, -halfW, halfW) }); // GK hugs the line, tracks ball y lightly
      continue;
    }
    const role = s.role === 'DEF' ? 'DEF' : s.role === 'FWD' ? 'FWD' : 'MID';
    let nx = s.base.x + phaseShift + SH.LONG[role] * ballXNorm; // slide WITH the ball, not toward it
    const ny = clamp(s.base.y + SH.LAT[role] * ballYNorm, -0.95, 0.95);
    // defending: DEF/MID never stand upfield of the ball line — the block is goal-side by contract
    if (phase === 'DEFEND' && role !== 'FWD') {
      const gap = role === 'DEF' ? SH.GOALSIDE_GAP.DEF : SH.GOALSIDE_GAP.MID;
      nx = Math.min(nx, ballXNorm - gap);
    }
    nx = clamp(nx, -0.95, 0.95);
    targets.set(id, { x: dir * nx * halfL, y: ny * halfW });
  }
  return targets;
}
